<!--
 * The Header
 * --------------------
 * Renders the header.
 *
 * @author  Harvinder Singh <harvinder@maropost.com>
 * @created 2025-09-12
 -->

<template>
  <TheHeader
    :notification-icon-props = "{
      onClick: openNotificationDrawer,
      count:unreadCount ?? 0,
      hideNotificationIcon: hideNotificationIcon
    }"
    :route-to-preferences="`${AppConfig.IDENTITY_BASE_URL}/#/preferences`"
    :username="userStore.username"
    hideAppBarNavIcon
    :logout="userStore.logout"
    :account-id="userStore.getAccountId"
  >
    <template #apps-dropdown>
      <v-img
        class="mx-8 mr-14 cursor-pointer"
        width="120px"
        max-width="140px"
        :src="maropostLogo"
        alt="Maropost logo"
        @click="navigateToDashboard()"
      /> 
    </template>

    <template #account-dropdown v-if="userStore.getAccountId">
      <AccountsDropDown />
    </template>

    <template #search-bar v-if="userStore.getAccountId && !isTrialExpired">
      <SearchBar />
    </template>
    
    <!-- passing empty template to hide the timezone from the header -->
    <template #timezone>
      <div>
        <v-tooltip text="Da Vinci" location="bottom">
          <template #activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              class="guide-cta__trigger cta-button"
               density="compact"
               size="medium"
              aria-label="What's new"
              @click="router.push('/preview/Da-Vinci')"
            >
              <v-icon color="black" size="20">mdi-creation</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
        <v-tooltip text="Maropost Galaxy" location="bottom">
          <template #activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              class="guide-cta__trigger cta-button"
              density="compact"
              size="medium"
              aria-label="Maropost Galaxy"
              target="_blank"
              rel="noopener noreferrer"
              href="https://galaxy.maropost.com/s/"
            >
              <v-icon color="black"  size="20">mdi-book-open-variant</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
        <v-tooltip text="Settings" location="bottom">
          <template #activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              class="guide-cta__trigger cta-button"
              density="compact"
              size="medium"
              aria-label="Settings"
              :href="`/accounts/${userStore.getAccountId}/settings`"
              @click="handleSettingsClick"
            >
              <v-icon color="black" size="20">mdi-cog</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </div>
    </template>

    <!-- Guide CTA --> 
    <GuideCTA />
    
    <template #user-dropdown>
      <v-divider vertical></v-divider>
      <UserDropDown/>
    </template>

  </TheHeader> 
</template>
<script setup lang="ts">
  import { TheHeader } from "@maropost-ui/liquidsky-ui"
  import { AppConfig } from "@/lib/config"
  import { computed, onMounted, nextTick, ref, watch, watchEffect } from "vue"
  import { useUserStore } from "@/lib/stores"
  import AccountsDropDown from "./AccountsDropDown.vue"
  import UserDropDown from "./UserDropDown.vue"
  import SearchBar from "./SearchBar.vue"
  import { DrawerHost } from "@/lib/composable/drawer/drawerHostSDK"
  import { GuideCTA } from "@/lib/components"

// @ts-ignore
  import maropostLogo from "@/assets/maropost-logo.svg"
  import NotificationsDrawer from "../drawers/NotificationsDrawer.vue"
  import { useNotificationService } from "@/lib/services"
  import { navigateToUrl } from "single-spa"
  import router from "@/lib/router/router"

  // --------------------------------------------------
  // Store + Services
  // --------------------------------------------------
  const userStore = useUserStore()
  const unreadCount = ref(0)

  /**
   * Navigate to the dashboard
   */
  const handleSettingsClick = (e: MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return
    e.preventDefault()
    navigateToUrl(`/accounts/${userStore.getAccountId}/settings`)
  }

  const navigateToDashboard = () => {
    let accountId = userStore.getAccountId
    if (accountId) {
      navigateToUrl(`/accounts/${accountId}/dashboard`)
    }
  }
  /**
   * Handler for update_unread_count event from NotificationsDrawer
   */
  const handleUpdateUnreadCount = (count: number): void => {
    unreadCount.value = count
  }

  /**
   * Check if the trial is expired
   */
  const isTrialExpired = computed(() => userStore.getIsTrailAccount && userStore.getIsTrialExpired);
  

  /**
   * Hide notification icon if user does not have access to marketing or commerce
   */

  const hideNotificationIcon = computed(() => {
    if (!userStore.getAccountId) return true;

    const hasMarketingAccess =
      userStore.getProductAccess("marketing") &&
      userStore.getProductAccessOnRoles("marketing");

    const hasCommerceAccess =
      userStore.getProductAccess("commerce") &&
      userStore.getProductAccessOnRoles("commerce");

    return (!hasMarketingAccess && !hasCommerceAccess) || isTrialExpired.value;
  });

  /**
   * Notifications
  */
  const notificationList = computed(() => useNotificationService.notifications?.value ?? [])
  /**
   * Get the drawer component instance
   */
  const getDrawerComponentInstance = async (): Promise<any> => {
    return new Promise<any>((resolve) => {
      DrawerHost.onReady(async (api) => {
        await nextTick()
        resolve(api.getComponentInstance())
      })
    })
  }

  /**
   * Function to open the notification drawer.
  */
  const openNotificationDrawer = async (): Promise<void> => {
    /**
     * Open the notification drawer and pass the notification list to the drawer
     */
    DrawerHost.open({
      component: "NotificationsDrawer",
      options: {
        width: 570,
      },
      props: {
        notificationList: notificationList.value,
        onUpdateUnreadCount: handleUpdateUnreadCount
      },
      onClose: async () => {
        const componentInstance = await getDrawerComponentInstance()
        if (componentInstance?.unmount) {
          await componentInstance.unmount()
        }
      }
    })

    // Call mount method on the NotificationsDrawer component
    const componentInstance = await getDrawerComponentInstance()
    if (componentInstance?.mount) {
      await componentInstance.mount()
    }
  }

  /**
   * Watch useNotificationService.meta and update the unread count when it changes
   */
  watch(() => useNotificationService.meta?.value, (newVal: Record<string, unknown> | undefined) => {
    handleUpdateUnreadCount((newVal?.unread_count as number) ?? 0)
  }, { immediate: true })

  onMounted(async () => {
    /**
     * Handle update unread count for global access
     * This is done to ensure the unread count is updated in the header when the notification drawer is opened
     * from the remote application
     */
    (window as any).handleUpdateUnreadCount = (count: number): void => {
      handleUpdateUnreadCount(count)
    }

    /**
     * onReady: Register NotificationDrawer with DrawerHost
     * This is done to ensure the NotificationDrawer is registered with DrawerHost when the component is mounted
     */
    DrawerHost.onReady((api) => {
      api.register('NotificationsDrawer', NotificationsDrawer)
    })
  })

</script>

<style scoped lang="scss">
  .cta-button {
    height: 64px !important;
    padding: 12px !important;
    width: 40px !important;
  }
</style>
